import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Typography,
  IconButton,
  TableSortLabel,
  CircularProgress,
  Alert,
  Pagination,
  Snackbar,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  Search,
  ArrowUpward,
  ArrowDownward,
  Refresh,
  Delete,
  FileDownload,
} from "@mui/icons-material";

const API_BASE_URL = `${process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL}`;

const NewsLetterTable = ({ sidebarOpen = true }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [deleteLoading, setDeleteLoading] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    subscriberId: null,
    subscriberEmail: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [exportLoading, setExportLoading] = useState(false);

  // Export to CSV function
  const exportToCSV = () => {
    try {
      setExportLoading(true);

      // Use filtered data for export
      const dataToExport = filteredData;

      if (dataToExport.length === 0) {
        setSnackbar({
          open: true,
          message: "No data available to export",
          severity: "warning",
        });
        return;
      }

      // Define CSV headers
      const headers = ["Subscriber ID", "Email", "Subscribed Date"];

      // Convert data to CSV format
      const csvData = dataToExport.map((row) => [
        row._id || "",
        row.email || "",
        row.createdAt ? new Date(row.createdAt).toLocaleString() : "",
      ]);

      // Combine headers and data
      const fullCSVData = [headers, ...csvData];

      // Convert to CSV string
      const csvString = fullCSVData
        .map((row) =>
          row
            .map((field) => {
              const stringField = String(field);
              if (
                stringField.includes(",") ||
                stringField.includes('"') ||
                stringField.includes("\n")
              ) {
                return `"${stringField.replace(/"/g, '""')}"`;
              }
              return stringField;
            })
            .join(",")
        )
        .join("\n");

      // Create and download file
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);

        // Generate filename with current date
        const currentDate = new Date().toISOString().split("T")[0];
        const filename = `blog-newsletter-subscribers-${currentDate}.csv`;
        link.setAttribute("download", filename);

        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSnackbar({
          open: true,
          message: `CSV file exported successfully (${dataToExport.length} records)`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
      setSnackbar({
        open: true,
        message: `Export failed: ${error.message}`,
        severity: "error",
      });
    } finally {
      setExportLoading(false);
    }
  };

  // Fetch data from API
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/blog-newsletter/all`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Handle response format
      if (result.success && result.data) {
        setData(result.data);
        setPagination((prev) => ({
          ...prev,
          totalItems: result.data.length,
          totalPages: Math.ceil(result.data.length / prev.itemsPerPage),
        }));
      } else if (Array.isArray(result)) {
        setData(result);
        setPagination((prev) => ({
          ...prev,
          totalItems: result.length,
          totalPages: Math.ceil(result.length / prev.itemsPerPage),
        }));
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(`Failed to load data: ${err.message}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data.filter(
      (item) =>
        item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item._id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // Calculate pagination for filtered data
  const paginatedData = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    const filtered = filteredData;

    const totalPages = Math.ceil(filtered.length / pagination.itemsPerPage);

    // Update pagination state
    setPagination((prev) => ({
      ...prev,
      totalPages: totalPages,
      totalItems: filtered.length,
    }));

    return filtered.slice(startIndex, endIndex);
  }, [filteredData, pagination.currentPage, pagination.itemsPerPage]);

  // Delete subscriber via API
  const deleteSubscriber = async (id) => {
    try {
      setDeleteLoading((prev) => ({ ...prev, [id]: true }));

      const response = await fetch(`${API_BASE_URL}/blog-newsletter/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove from local state
      setData((prevData) => prevData.filter((item) => item._id !== id));

      setSnackbar({
        open: true,
        message: "Subscriber deleted successfully",
        severity: "success",
      });

      // If current page becomes empty, go to previous page
      if (paginatedData.length === 1 && pagination.currentPage > 1) {
        setPagination((prev) => ({
          ...prev,
          currentPage: prev.currentPage - 1,
        }));
      }
    } catch (err) {
      console.error("Error deleting subscriber:", err);
      setSnackbar({
        open: true,
        message: `Delete failed: ${err.message}`,
        severity: "error",
      });
    } finally {
      setDeleteLoading((prev) => ({ ...prev, [id]: false }));
      setDeleteDialog({
        open: false,
        subscriberId: null,
        subscriberEmail: "",
      });
    }
  };

  // Handle delete click
  const handleDeleteClick = (id, email) => {
    setDeleteDialog({
      open: true,
      subscriberId: id,
      subscriberEmail: email,
    });
  };

  // Handle delete confirm
  const handleDeleteConfirm = () => {
    if (deleteDialog.subscriberId) {
      deleteSubscriber(deleteDialog.subscriberId);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      subscriberId: null,
      subscriberEmail: "",
    });
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  // Handle refresh
  const handleRefresh = async () => {
    await fetchData();
    setSnackbar({
      open: true,
      message: "Data refreshed successfully",
      severity: "success",
    });
  };

  // Sort data locally
  const sortedData = useMemo(() => {
    return [...paginatedData].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);

      if (sortOrder === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  }, [paginatedData, sortOrder]);

  const handleSortChange = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  // Date formatting function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Calculate sidebar width
  const sidebarWidth = sidebarOpen ? 220 : 100;

  // Skeleton Row Component
  const SkeletonRow = () => (
    <TableRow
      sx={{
        "&:nth-of-type(even)": {
          bgcolor: "#f5f5f5",
        },
      }}
    >
      <TableCell sx={{ minWidth: 80 }}>
        <Skeleton variant="text" width="60%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        <Skeleton variant="text" width="85%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 260 }}>
        <Skeleton variant="text" width="90%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 180 }}>
        <Skeleton variant="text" width="80%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 120, textAlign: "center" }}>
        <Skeleton
          variant="circular"
          width={28}
          height={28}
          sx={{ mx: "auto" }}
        />
      </TableCell>
    </TableRow>
  );

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: "#fafafa",
        minHeight: "100vh",
        ml: {
          xs: "0px",
          sm: "0px",
          md: `${sidebarWidth}px`,
          xl: `${sidebarWidth}px`,
        },
        transition: "margin-left 0.3s ease",
        fontFamily: "Outfit",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: "#9048FB",
            fontWeight: 600,
            fontFamily: "Outfit",
          }}
        >
          Newsletter Subscribers
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Export to CSV">
            <IconButton
              onClick={exportToCSV}
              disabled={exportLoading || loading}
              sx={{
                bgcolor: "#4caf50",
                color: "white",
                "&:hover": { bgcolor: "#45a049" },
                "&:disabled": {
                  bgcolor: "rgba(76, 175, 80, 0.3)",
                  color: "rgba(255, 255, 255, 0.5)",
                },
              }}
            >
              {exportLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FileDownload />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh data">
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                bgcolor: "#9048FB",
                color: "white",
                "&:hover": { bgcolor: "#7c3aed" },
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={fetchData}
              sx={{ fontFamily: "Outfit" }}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Search and Sort Controls */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search by email ..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            maxWidth: 600,
            backgroundColor: "white",
            "& .MuiInputBase-input": {
              fontFamily: "Outfit",
              fontSize: "16px",
            },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "Outfit" }}
          >
            Sort:
          </Typography>
          <IconButton
            onClick={handleSortChange}
            sx={{
              bgcolor: "#9048FB",
              color: "white",
              "&:hover": { bgcolor: "#7c3aed" },
            }}
          >
            {sortOrder === "desc" ? <ArrowDownward /> : <ArrowUpward />}
          </IconButton>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "Outfit" }}
          >
            {sortOrder === "desc" ? "Newest" : "Oldest"}
          </Typography>
        </Box>
      </Box>

      {/* Data Table */}
      <Paper sx={{ overflow: "hidden", boxShadow: 2 }}>
        <TableContainer
          sx={{
            position: "relative",
            maxHeight: 700,
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a1a1a1",
            },
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ bgcolor: "#9048FB" }}>
              <TableRow>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 80,
                    whiteSpace: "nowrap",
                  }}
                >
                  #
                </TableCell>
                {/* <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 200,
                    whiteSpace: "nowrap",
                  }}
                >
                  Subscriber ID
                </TableCell> */}
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 260,
                    whiteSpace: "nowrap",
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 180,
                    whiteSpace: "nowrap",
                  }}
                >
                  <TableSortLabel
                    active={true}
                    direction={sortOrder}
                    onClick={handleSortChange}
                    sx={{
                      color: "white !important",
                      fontFamily: "Outfit",
                      "& .MuiTableSortLabel-icon": {
                        color: "white !important",
                      },
                    }}
                  >
                    Subscribed Date
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 120,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Show skeleton rows when loading
                Array.from({ length: 8 }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} />
                ))
              ) : sortedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      textAlign: "center",
                      py: 4,
                      color: "text.secondary",
                      fontFamily: "Outfit",
                      fontSize: "16px",
                    }}
                  >
                    {searchTerm
                      ? "No subscribers found"
                      : "No subscribers available"}
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row, index) => (
                  <TableRow
                    key={row._id}
                    sx={{
                      "&:nth-of-type(even)": {
                        bgcolor: "#f5f5f5",
                      },
                      "&:hover": {
                        bgcolor: "#f3f0ff",
                      },
                    }}
                  >
                    {/* Row Number */}
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontFamily: "Outfit",
                        fontSize: "16px",
                        color: "#666",
                      }}
                    >
                      {(pagination.currentPage - 1) * pagination.itemsPerPage +
                        index +
                        1}
                    </TableCell>

                    {/* Subscriber ID */}
                    {/* <TableCell
                      sx={{
                        fontWeight: 500,
                        fontFamily: "Outfit",
                        fontSize: "14px",
                        color: "#9048FB",
                        wordBreak: "break-all",
                        maxWidth: 200,
                      }}
                    >
                      <Tooltip title={row._id}>
                        <span>{row._id || "N/A"}</span>
                      </Tooltip>
                    </TableCell> */}

                    {/* Email */}
                    <TableCell
                      sx={{
                        color: "#9048FB",
                        fontFamily: "Outfit",
                        fontSize: "16px",
                        fontWeight: 500,
                        wordBreak: "break-word",
                      }}
                    >
                      {row.email || "N/A"}
                    </TableCell>

                    {/* Subscribed Date */}
                    <TableCell
                      sx={{
                        color: "#A3AED0",
                        fontFamily: "Outfit",
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDate(row.createdAt)}
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ textAlign: "center" }}>
                      <Tooltip title="Delete subscriber">
                        <IconButton
                          onClick={() => handleDeleteClick(row._id, row.email)}
                          disabled={deleteLoading[row._id]}
                          sx={{
                            color: "#f44336",
                            "&:hover": {
                              backgroundColor: "rgba(244, 67, 54, 0.1)",
                            },
                            "&:disabled": {
                              color: "rgba(244, 67, 54, 0.3)",
                            },
                          }}
                        >
                          {deleteLoading[row._id] ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <Delete fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination and Results Summary */}
      <Box
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: "Outfit", fontSize: "16px" }}
        >
          {loading ? (
            <Skeleton variant="text" width={250} height={24} />
          ) : (
            <>
              Showing {sortedData.length} of {pagination.totalItems} subscribers
              {searchTerm && ` (filtered by "${searchTerm}")`}
            </>
          )}
        </Typography>

        {pagination.totalPages > 1 && !loading && (
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#9048FB",
                fontFamily: "Outfit",
                fontSize: "16px",
                "&.Mui-selected": {
                  backgroundColor: "#9048FB",
                  color: "white",
                },
                "&:hover": {
                  backgroundColor: "#f3f0ff",
                },
              },
            }}
            disabled={loading}
          />
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 600,
            color: "#f44336",
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="delete-dialog-description"
            sx={{
              fontFamily: "Outfit",
              fontSize: "16px",
            }}
          >
            Are you sure you want to delete the subscriber{" "}
            <strong>{deleteDialog.subscriberEmail}</strong>? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              fontFamily: "Outfit",
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              fontFamily: "Outfit",
              backgroundColor: "#f44336",
              "&:hover": {
                backgroundColor: "#d32f2f",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontFamily: "Outfit",
            fontSize: "16px",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewsLetterTable;
